import { AdminProfessionals } from '@platform/client';
import { AdminProMatchRecord } from '@platform/client/schemas';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useContext, useMemo } from 'react';
import { formatDateStr } from '../../../dashboard/my-projects/utils';
import { NoRecordsFoundTabContent, ProfileTabContainer } from '../../components/ProfileComponents';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { OrganizationLink } from '../organizations/OrganizationsAdmin';
import { ProjectLink } from '../projects/ProjectsAdmin';
import { IntroductionLink } from '../introductions/IntroductionsAdmin';

const matchColumnHelper = createColumnHelper<AdminProMatchRecord>();

export const MatchingTabContent = ({ proId }: { proId: number }) => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const {
    data: matchData,
    isLoading,
    isError,
    isSuccess,
  } = AdminProfessionals.useGetProsMatchingData({
    user_id: proId,
    page: adminQueryOptionsV2.page,
    limit: adminQueryOptionsV2.limit,
    sort: adminQueryOptionsV2.sort,
  });
  const columns = useMemo(
    () =>
      [
        matchColumnHelper.accessor('introductionId', {
          header: 'Intro Id',
          cell: (info) => (
            <IntroductionLink
              introductionId={info.getValue()}
              introductionName={info.getValue().toString()}
            />
          ),
          size: 5,
        }),
        matchColumnHelper.accessor('introductionInitiationType', {
          header: 'Intro Type',
          cell: (info) => (info.getValue() === 'Experteer' ? 'Sent' : 'Received'),
          size: 10,
        }),
        matchColumnHelper.accessor('introductionCreatedAt', {
          header: 'Date',
          cell: (info) => formatDateStr(info.getValue()),
          size: 1,
        }),
        matchColumnHelper.accessor('organizationName', {
          header: 'Organization',
          cell: ({ row }) => (
            <OrganizationLink
              organizationId={row.original.organizationId}
              organizationName={row.original.organizationName}
            />
          ),
        }),
        matchColumnHelper.accessor('projectName', {
          header: 'Project',
          cell: ({ row }) =>
            row.original.projectId ?
              <ProjectLink
                projectId={row.original.projectId}
                projectName={row.original.projectName}
              />
            : null,
          size: 300,
        }),
        matchColumnHelper.accessor('myProjectsStatus', {
          header: 'Status',
          cell: (info) => info.getValue(),
        }),
        matchColumnHelper.accessor('myProjectsStatusDate', {
          header: 'Status date',
          cell: (info) => formatDateStr(info.getValue()),
          size: 10,
        }),
      ] as ColumnDef<AdminProMatchRecord>[],
    [],
  );

  return (
    <ProfileTabContainer>
      {isSuccess && matchData?.matchRecords.length === 0 ?
        <NoRecordsFoundTabContent recordType='professional' />
      : <AdminTable
          isError={isError}
          isLoading={isLoading}
          isSuccess={isSuccess}
          columns={columns}
          data={matchData?.matchRecords || []}
          isSimple
          filteredCount={matchData?.totalCount ?? 0}
        />
      }
    </ProfileTabContainer>
  );
};
